import { Component, OnInit, inject } from '@angular/core';
import {
  buildOfficerSelectOptions,
  dispatchCompaniesHouseSelectedOfficer,
  mapOfficerToDirectorFields,
} from '@app/finxone-web-frontend/app/lib/utils/companies-house-officer.utils';
import {
  buildNestedPatch,
  fillBlanks,
  getNestedValue,
} from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { normalisePrimaryOfficer } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/onboarding-zone/business-onboarding-actions.utils';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { FormActionTypeEnum, FormID } from '@finxone-platform/form-action';
import {
  AppBuilderPreviewState,
  BaseCssConfig,
  BaseCssProperties,
  BaseWidgetProperties,
  COMPANIES_HOUSE_REQUIRED_FIELDS,
  CompaniesHouseAddress,
  CompaniesHouseOfficer,
  LookupsCommonType,
  ProviderClass,
  SKELETON_LAYOUT_CONFIG_PATH,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  resolveSkeletonLoadingTemplate,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil, tap } from 'rxjs';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { CountriesCurrenciesService, IconCacheService } from '@finxone-platform/shared/services';
import { GenericHandleBarModel } from '@finxone-platform/ui-components';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-companies-house-director-selection-widget',
  templateUrl: './companies-house-director-selection-widget.component.html',
})
export class CompaniesHouseDirectorSelectionWidgetComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public officerOptions: LookupsCommonType[] = [];
  public selectedValue: string | number | undefined;
  public baseCssConfig: BaseCssConfig;
  public contentStyle: baseCssConfigStyle;
  public widgetProperties: BaseWidgetProperties;
  public hasOfficers = false;
  public isLoading = false;
  public noResultsContent = '';
  public fetchingTemplate = '';
  public appBuilderShowFetchingContent = false;

  private baseHandlebarModel: GenericHandleBarModel;
  private noResultsTemplateFunction: HandlebarsTemplateDelegate<unknown> = () => '';
  private fetchingTemplateFunction: HandlebarsTemplateDelegate<unknown> = () => '';

  public dataIn: ProviderClass<UiZoneWidgetAttributeConfig> = inject(ProviderClass);

  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly workflowFormsService = inject(WorkflowFormsService);
  private readonly navigationService = inject(NavigationService);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);

  private formConfig: { parentFormId: string; childFormId: string };

  constructor() {
    this.buildBaseHandlebarModel();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.initStyles();
    this.initWidgetProperties();
    this.setAppBuilderPreviewState();
    this.loadOfficers();
  }

  /**
   * Preview states belong to the app builder canvas only, so a project published while Fetching was
   * selected still shows a customer the real loading state. The canvas has no officers in form
   * state to load, so this is also what puts a designed skeleton on show while it is being designed.
   */
  private setAppBuilderPreviewState(): void {
    this.appBuilderShowFetchingContent =
      this.configService.getActiveConfigType().isWorkflow &&
      this.widgetProperties?.['appBuilderState'] === AppBuilderPreviewState.FETCHING;
  }

  public get showFetchingState(): boolean {
    return this.isLoading || this.appBuilderShowFetchingContent;
  }

  private buildBaseHandlebarModel() {
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(
        tap((model) => {
          this.baseHandlebarModel = model;
          this.updateTemplates();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private updateTemplates() {
    this.fetchingTemplate = resolveSkeletonLoadingTemplate(
      this.dataIn?.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
      this.fetchingTemplateFunction(this.baseHandlebarModel),
    );
    this.noResultsContent = this.hasOfficers ? '' : this.noResultsTemplateFunction(this.baseHandlebarModel);
  }

  public buildHandlebarCustomModel(option: LookupsCommonType, index: number): Record<string, unknown> {
    return {
      officer: { ...(option.metadata as Record<string, unknown>), name: option.label },
      label: option.label,
      value: option.value,
      index,
      directorDetailsValid: this.verifyRequiredFieldsAndReturnStatus(
        option?.metadata as CompaniesHouseOfficer,
      ),
    };
  }

  private verifyRequiredFieldsAndReturnStatus(officer: CompaniesHouseOfficer): boolean {
    const requiredFields = this.widgetProperties?.['selectedRequiredFields'];
    if (!requiredFields || requiredFields.length === 0) {
      return true;
    }

    const addressFields: Set<keyof CompaniesHouseAddress> = new Set(
      COMPANIES_HOUSE_REQUIRED_FIELDS.find((f) => f.key === 'address')?.children?.map(
        (child) => (child.data as { id: string })?.id as keyof CompaniesHouseAddress,
      ) ?? [],
    );

    // Check if all required fields are present and not empty
    for (const field of requiredFields) {
      let value: unknown;

      if (addressFields.has(field as keyof CompaniesHouseAddress)) {
        // Check nested address object for address-related fields
        value = officer.address?.[field as keyof CompaniesHouseAddress];
      } else {
        // Check top-level fields
        value = (officer as Record<string, unknown>)?.[field];
      }

      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return false;
      }
    }
    return true;
  }

  private initStyles(): void {
    this.baseCssConfig = this.dataIn.attri?.css_config;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLORS_V2,
      BaseCssProperties.BORDER_V2,
      BaseCssProperties.BOX_SHADOW,
    ]);
  }

  private initWidgetProperties(): void {
    this.widgetProperties = this.dataIn?.attri?.widgetProperties ?? {};
    this.formConfig = {
      parentFormId: this.dataIn.attri?.['parentFormId'],
      childFormId: this.dataIn.attri?.['childFormId'] === 'root' ? '' : this.dataIn.attri?.['childFormId'],
    };
    this.noResultsTemplateFunction = Handlebars.compile(this.dataIn?.attri?.['noRecordsTemplate'] ?? '');
    this.fetchingTemplateFunction = Handlebars.compile(this.widgetProperties?.['loadingTextContent'] ?? '');
  }

  private loadOfficers(): void {
    const formState = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(this.formConfig.parentFormId),
    )?.formData;
    const officers = (formState?.[this.formConfig.childFormId] as CompaniesHouseOfficer[]) ?? [];

    this.hasOfficers = !!officers?.length;
    this.officerOptions = this.hasOfficers ? buildOfficerSelectOptions(officers) : [];
    if (
      this.widgetProperties['companyDirectorsListingcontext'] &&
      this.widgetProperties['companyDirectorsListingcontext'] !== 'all'
    ) {
      this.filterOutSelectedDirectorFromList();
    }
    this.updateTemplates();
    this.isLoading = false;
  }

  private filterOutSelectedDirectorFromList() {
    const formState = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(this.formConfig.parentFormId),
    )?.formData;
    const primaryOfficer = formState?.['primaryOfficer'];
    const officers = (formState?.[this.formConfig.childFormId] as CompaniesHouseOfficer[]) ?? [];
    if (this.officerOptions?.length && officers) {
      this.officerOptions = this.officerOptions.filter(
        (officer) => officer.metadata?.['uniqueId'] !== primaryOfficer?.director_metadata?.uniqueId,
      );
    }
  }

  public onSelectOfficer(option: LookupsCommonType): void {
    this.selectedValue = option.value;

    const index = this.parseOfficerIndex(option.value);
    const officer = index !== null ? this.getMemberAt(index) : null;

    officer && (officer.director = option.value as string);

    if (index !== null && officer) {
      this.seedDirectorArrayItem(index, officer);
    }

    this.navigate();
  }

  /** option.value is "officer-<index>" for real selections, "companies_house_none_of_the_above" otherwise. */
  private parseOfficerIndex(value: string | number | undefined): number | null {
    const match = /^officer-(\d+)$/.exec(String(value ?? ''));
    return match ? Number(match[1]) : null;
  }

  private getMemberAt(index: number): CompaniesHouseOfficer | null {
    const formState = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW),
    );
    const members = (formState?.formData?.['members'] as CompaniesHouseOfficer[]) ?? [];
    return members[index] ?? null;
  }

  /**
   * Pins the array-edit-index to the selected officer's slot and merges the wizard's own field
   * shape (name/nationality/officerBirthMonth+Year/nested address) into members[index], so
   * navigating to the director wizard prefills correctly - CH's raw dateOfBirth/address fields
   * are seeded onto members verbatim by CompaniesHouseListingWidgetComponent and don't match the
   * wizard's control names/nesting on their own.
   *
   * Who is primary is decided elsewhere and stored at businessOnboardingFlow.primaryOfficer
   * (not by this widget) - every member's isPrimary is (re)derived here by matching its
   * `director` selection value against primaryOfficer.director, so the array stays consistent
   * with that object regardless of whether it was set before or after this officer was picked.
   * When primaryOfficer carries data for a matched member (e.g. collected on a later wizard
   * page), that data is folded into the member here too, mirroring
   * transformBusinessFormDataIntoRequestPayload's merge, so members[] doesn't go stale until
   * final submit re-derives it.
   */
  private seedDirectorArrayItem(index: number, officer: CompaniesHouseOfficer): void {
    const rootFormID = FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW;
    const childKey = 'members';
    const arrayStateKey = `${rootFormID}.${childKey}`;

    this.workflowFormsService.setActiveFormArrayIndex(arrayStateKey, index);

    const currentFormData = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(rootFormID),
    )?.formData;
    const primaryOfficer = normalisePrimaryOfficer(
      getNestedValue<Record<string, unknown>>(currentFormData, 'primaryOfficer'),
    );
    const primaryOfficerId = primaryOfficer?.['director'];
    const isPrimary = (member: Record<string, unknown>) =>
      !!primaryOfficerId && member['director'] === primaryOfficerId;
    const mergePrimaryOfficer = (member: Record<string, unknown>) =>
      primaryOfficer && isPrimary(member) ? fillBlanks(primaryOfficer, member) : member;

    const members = (getNestedValue<Record<string, unknown>[]>(currentFormData, childKey) ?? []).map(
      (member: Record<string, unknown>, i: number) =>
        i === index ? member : { ...mergePrimaryOfficer(member), isPrimary: isPrimary(member) },
    );
    // fillBlanks (not a plain spread) so re-selecting the same officer later never clobbers
    // fields the user has already edited since the last time this director was seeded.
    members[index] = {
      ...mergePrimaryOfficer(fillBlanks(members[index], mapOfficerToDirectorFields(officer))),
      isPrimary: isPrimary(officer),
    };

    dispatchCompaniesHouseSelectedOfficer(this.store, members[index] as CompaniesHouseOfficer);

    const targetFormId = FormID.BUSINESS_ONBOARDING_FLOW;
    if (targetFormId) {
      this.workflowFormsService.patchFormValues(targetFormId, members[index]);
    }

    this.store.dispatch(
      new SetFormActionWithId(
        { type: '', formData: buildNestedPatch(currentFormData, childKey, members) },
        rootFormID,
      ),
    );
  }

  private navigate(): void {
    const attri = this.dataIn.attri;
    const pageId: string = attri?.['pageId'] ?? '';
    if (!pageId) return;

    const { pageUrl } = this.navigationService.buildPageNavigationUrl({
      pageId,
      zone: attri?.['zone'] ?? '',
      pageUrl: attri?.['pageUrl'] ?? '',
      role: attri?.['role'] ?? '',
      navigationOption: attri?.['navigationOption'],
      tabRef: attri?.['tabRef'] ?? '',
      pageTransition: attri?.['pageTransition'],
    });

    this.navigationService.navigateByPageUrl({
      url: pageUrl,
      targetRole: attri?.['role'] ?? '',
      currentRole: this.store.selectSnapshot(RoleState.getRole)?.role,
      zone: attri?.['zone'] ?? '',
      externalLink: attri?.['externalUrl'],
      browserBehaviour: attri?.['browserBehaviour'],
    });
  }
}
